import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
    FaBook,
    FaHome,
    FaFilter,
    FaTable,
    FaChartBar,
    FaChartArea,
    FaCalculator,
    FaDownload,
    FaUser,
    FaUsers,
    FaShieldAlt,
    FaPlay,
    FaSearch,
    FaCog,
    FaClipboardList,
    FaFlask
} from 'react-icons/fa';
import { COLOR_PALETTE } from '../utils/colorConstants';

// Helper: renderiza uma figura com legenda
const Fig = ({ n, alt }) => (
    <div style={{ margin: '1.5rem 0', width: '100%', textAlign: 'center' }}>
        <img
            src={`/manual/imagem${n}.jpg`}
            alt={alt}
            style={{
                display: 'inline-block',
                maxWidth: '80%',
                borderRadius: '8px',
            }}
        />
        <p style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '0.5rem' }}>
            {alt}
        </p>
    </div>
);

const UserManualModal = ({ show, onHide }) => {
    const [activeSection, setActiveSection] = useState('intro');
    const contentRef = useRef(null);

    const sections = [
        { id: 'intro', label: 'Introdução', icon: FaBook },
        { id: 'acesso', label: 'Acesso ao sistema', icon: FaShieldAlt },
        { id: 'principal', label: 'Painel principal', icon: FaHome },
        { id: 'filtros', label: 'Painel de filtros', icon: FaFilter },
        { id: 'resultados', label: 'Painel de resultados', icon: FaClipboardList },
        { id: 'resumo', label: 'Resumo', icon: FaTable },
        { id: 'estatisticas', label: 'Estatística descritiva', icon: FaChartBar },
        { id: 'distribuicoes', label: 'Distribuições', icon: FaChartArea },
        { id: 'simulacao', label: 'Simulação', icon: FaCalculator },
        { id: 'exportar', label: 'Exportar resultados', icon: FaDownload },
        { id: 'perfil', label: 'Meu Perfil', icon: FaUser },
        { id: 'bancoDados', label: 'Banco de Dados', icon: FaCog },
        { id: 'gerenciarUsuarios', label: 'Gerenciar Usuários', icon: FaUsers },
        { id: 'experimental', label: 'Experimental', icon: FaFlask }
    ];

    useEffect(() => {
        if (!show || !contentRef.current) return;

        const handleScroll = () => {
            const scrollPosition = contentRef.current.scrollTop + 100;

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = document.getElementById(sections[i].id);
                if (section && section.offsetTop <= scrollPosition) {
                    setActiveSection(sections[i].id);
                    break;
                }
            }
        };

        const content = contentRef.current;
        content.addEventListener('scroll', handleScroll);
        return () => content.removeEventListener('scroll', handleScroll);
    }, [show]);

    const scrollToSection = (sectionId) => {
        const section = document.getElementById(sectionId);
        if (section && contentRef.current) {
            const offsetTop = section.offsetTop - 20;
            contentRef.current.scrollTo({
                top: offsetTop,
                behavior: 'smooth'
            });
            setActiveSection(sectionId);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header
                closeButton
                // style={{
                //     backgroundColor: COLOR_PALETTE.primary,
                //     color: 'white',
                //     borderBottom: 'none'
                // }}
                className='bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4'
            >
                <Modal.Title className="d-flex align-items-center gap-2">
                    <FaBook size={24} />
                    <span>Manual do Usuário - <strong>DInoUSP</strong></span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ padding: 0, height: '70vh', display: 'flex' }}>
                {/* Sidebar de Navegação */}
                <div style={{
                    width: '280px',
                    backgroundColor: '#f8f9fa',
                    borderRight: '1px solid #dee2e6',
                    overflowY: 'auto',
                    padding: '1.5rem 0'
                }}>
                    <nav>
                        {sections.map((section) => {
                            const Icon = section.icon;
                            const isActive = activeSection === section.id;
                            return (
                                <button
                                    key={section.id}
                                    onClick={() => scrollToSection(section.id)}
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '0.75rem 1.5rem',
                                        border: 'none',
                                        background: isActive ? COLOR_PALETTE.primary : 'transparent',
                                        color: isActive ? 'white' : '#495057',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        fontSize: '0.95rem',
                                        fontWeight: isActive ? '600' : '400',
                                        borderLeft: isActive ? `4px solid ${COLOR_PALETTE.primary}` : '4px solid transparent'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.backgroundColor = '#e9ecef';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isActive) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    <Icon size={16} />
                                    <span>{section.label}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Conteúdo Principal */}
                <div
                    ref={contentRef}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '2rem 3rem',
                        backgroundColor: 'white'
                    }}
                >
                    {/* ── Seção 1: Introdução ── */}
                    <section id="intro" style={{ marginBottom: '3rem' }}>
                        <h3 style={{
                            color: COLOR_PALETTE.primary,
                            marginBottom: '1.5rem',
                            fontSize: '1.5rem',
                            fontWeight: '600',
                            borderBottom: `3px solid ${COLOR_PALETTE.primary}`,
                            paddingBottom: '0.5rem'
                        }}>
                            1. Introdução
                        </h3>
                        <p>
                            O DInoUSP é um sistema que permite o usuário realizar análises de semelhança a partir de dados
                            históricos de projetos de inovação tecnológica. Sendo capaz de auxiliar atividades de planejamento
                            relacionadas à progressão de níveis de prontidão TRL (<em>Technology Readiness Level</em>) e CRL (<em>Commercial Readiness Level</em>).
                        </p>

                        <div style={{
                            background: '#f8f9fa',
                            padding: '1rem',
                            borderRadius: '8px',
                            borderLeft: `4px solid ${COLOR_PALETTE.primary}`,
                            marginBottom: '1.5rem'
                        }}>
                            <strong style={{ color: COLOR_PALETTE.primary }}>São 3 as entradas utilizadas:</strong>
                            <ul className="mt-2 mb-0" style={{
                                color: '#495057',
                                paddingLeft: '2rem',
                                listStyleType: 'disc',
                                listStylePosition: 'outside'
                            }}>
                                <li style={{ marginBottom: '0.25rem' }}>Dados e <strong>características</strong> de projetos de inovação tecnológica;</li>
                                <li style={{ marginBottom: '0.25rem' }}>Histórico e cronograma da progressão em <strong>níveis de prontidão</strong> de cada projeto;</li>
                                <li style={{ marginBottom: '0.25rem' }}><strong>Orçamentos</strong> (planejado e realizado) de cada projeto.</li>
                            </ul>
                        </div>

                        <div style={{
                            background: '#f8f9fa',
                            padding: '1rem',
                            borderRadius: '8px',
                            borderLeft: `4px solid ${COLOR_PALETTE.primary}`
                        }}>
                            <strong style={{ color: COLOR_PALETTE.primary }}>E dentre os outputs oferecidos, os principais são:</strong>
                            <ul className="mt-2 mb-0" style={{
                                color: '#495057',
                                paddingLeft: '2rem',
                                listStyleType: 'disc',
                                listStylePosition: 'outside'
                            }}>
                                <li style={{ marginBottom: '0.25rem' }}><strong>Estatísticas básicas</strong> para a progressão em níveis de prontidão dos projetos semelhantes (contagem, média, desvio padrão...);</li>
                                <li style={{ marginBottom: '0.25rem' }}>Ajuste em <strong>distribuições</strong> da progressão individual dos níveis de prontidão;</li>
                                <li style={{ marginBottom: '0.25rem' }}><strong>Simulações</strong> da duração total de um projeto num intervalo de níveis de prontidão desejado.</li>
                            </ul>
                        </div>
                    </section>

                    {/* ── Seção 2: Acesso ao sistema ── */}
                    <section id="acesso" style={{ marginBottom: '3rem' }}>
                        <h3 style={{
                            color: COLOR_PALETTE.primary,
                            marginBottom: '1.5rem',
                            fontSize: '1.5rem',
                            fontWeight: '600',
                            borderBottom: `3px solid ${COLOR_PALETTE.primary}`,
                            paddingBottom: '0.5rem'
                        }}>
                            2. Acesso ao sistema
                        </h3>

                        <h5 style={{ color: COLOR_PALETTE.primary, marginTop: '2rem', marginBottom: '1rem' }}>
                            2.1. Tela de Login
                        </h5>
                        <p>
                            A janela de acesso ao sistema (exposta na Figura 1) é o primeiro ponto de contato com o DInoUSP.
                            Este ambiente garante a segurança dos dados e o controle de acesso às ferramentas de análise,
                            feito por meio de <em>login</em> com usuário e senha padrão.
                        </p>
                        <Fig n={1} alt="Figura 1: Página de login do DInoUSP" />

                        <h5 style={{ color: COLOR_PALETTE.primary, marginTop: '2rem', marginBottom: '1rem' }}>
                            2.2. Cadastro de novo usuário
                        </h5>
                        <p>
                            Caso o usuário ainda não possua cadastro, é possível realizá-lo clicando no botão <strong>Novo usuário</strong>,
                            que leva para a página exibida na Figura 2. Para concluir o cadastro, o usuário deve preencher as seguintes informações:
                        </p>
                        <ul style={{ color: '#495057', paddingLeft: '2rem', listStyleType: 'disc', listStylePosition: 'outside' }}>
                            <li style={{ marginBottom: '0.25rem' }}>Nome completo</li>
                            <li style={{ marginBottom: '0.25rem' }}>E-mail institucional</li>
                            <li style={{ marginBottom: '0.25rem' }}><em>(Opcional)</em> Número de telefone para contato</li>
                            <li style={{ marginBottom: '0.25rem' }}><em>(Opcional)</em> Chave de acesso</li>
                            <li style={{ marginBottom: '0.25rem' }}>Senha segura</li>
                        </ul>
                        <Fig n={2} alt="Figura 2: Página de cadastro do DInoUSP" />

                        <h5 style={{ color: COLOR_PALETTE.primary, marginTop: '2rem', marginBottom: '1rem' }}>
                            2.3. Níveis de acesso
                        </h5>
                        <p>O DInoUSP possui três (3) níveis de acesso:</p>

                        <div className="mb-3">
                            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', borderLeft: `4px solid ${COLOR_PALETTE.primary}`, marginBottom: '1rem' }}>
                                <h6 style={{ color: COLOR_PALETTE.primary, marginBottom: '0.5rem' }}>
                                    <FaShieldAlt className="me-2" />1. Usuário administrador
                                </h6>
                                <p style={{ marginBottom: 0, color: '#495057' }}>
                                    Usuário com todas as permissões de acesso e permissões gerenciais do <em>software</em>.
                                </p>
                            </div>
                            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', borderLeft: `4px solid ${COLOR_PALETTE.primary}`, marginBottom: '1rem' }}>
                                <h6 style={{ color: COLOR_PALETTE.primary, marginBottom: '0.5rem' }}>
                                    <FaUser className="me-2" />2. Usuário verificado
                                </h6>
                                <p style={{ marginBottom: 0, color: '#495057' }}>
                                    Usuário com permissão de acesso às funcionalidades básicas do <em>software</em>.
                                </p>
                            </div>
                            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', borderLeft: `4px solid ${COLOR_PALETTE.primary}` }}>
                                <h6 style={{ color: COLOR_PALETTE.primary, marginBottom: '0.5rem' }}>
                                    <FaUsers className="me-2" />3. Usuário não verificado
                                </h6>
                                <p style={{ marginBottom: 0, color: '#495057' }}>
                                    Usuário cadastrado no sistema com permissão de acesso pendente. Não pode usar as funcionalidades do <em>software</em>.
                                </p>
                            </div>
                        </div>
                        <p style={{ marginTop: '1rem' }}>
                            Um usuário recém cadastrado será alocado na categoria <strong>não verificado</strong> até que um administrador libere seu acesso.
                        </p>
                    </section>

                    {/* ── Seção 3: Painel principal ── */}
                    <section id="principal" style={{ marginBottom: '3rem' }}>
                        <h3 style={{
                            color: COLOR_PALETTE.primary,
                            marginBottom: '1.5rem',
                            fontSize: '1.5rem',
                            fontWeight: '600',
                            borderBottom: `3px solid ${COLOR_PALETTE.primary}`,
                            paddingBottom: '0.5rem'
                        }}>
                            3. Painel principal
                        </h3>
                        <p>
                            Ao efetuar o <em>login</em>, o usuário acessa o painel principal do sistema que contém três (3) painéis
                            apresentados na Figura 3: o cabeçalho, painel de filtros e painel de resultados.
                        </p>
                        <Fig n={3} alt="Figura 3: Janela principal do DInoUSP" />
                        <p>
                            <strong>OBS:</strong> Caso o usuário não tenha a permissão necessária para usar a ferramenta, uma mensagem
                            de erro aparecerá no lugar dos filtros, conforme a Figura 4.
                        </p>
                        <Fig n={4} alt="Figura 4: Janela principal, para usuário não verificado, do DInoUSP" />

                        <h5 style={{ color: COLOR_PALETTE.primary, marginTop: '2rem', marginBottom: '1rem' }}>
                            3.1. Cabeçalho
                        </h5>
                        <p>
                            O cabeçalho (Figura 5) apresenta informações de título da ferramenta e permite acessar funções
                            complementares, que serão detalhadas posteriormente. Ele está presente em todas as janelas do
                            sistema, exceto na página de <em>login</em>, sendo que ao clicar no logo do DInoUSP o usuário retorna à
                            janela principal do <em>software</em>.
                        </p>
                        <Fig n={5} alt="Figura 5: Cabeçalho do DInoUSP" />
                        <p>
                            À direita do cabeçalho, um <em>Chevron</em> (˅), permite abrir um menu auxiliar (Figura 6 a Figura 8) que
                            apresenta informações do usuário e permite acessar outras funcionalidades, dependendo do nível de acesso. São elas:
                        </p>
                        <ul style={{ color: '#495057', paddingLeft: '2rem', listStyleType: 'disc', listStylePosition: 'outside' }}>
                            <li style={{ marginBottom: '0.25rem' }}><strong>Meu perfil:</strong> Para atualizar dados pessoais do usuário. Acessível para todos;</li>
                            <li style={{ marginBottom: '0.25rem' }}><strong>Banco de dados:</strong> Para atualizar os dados e visualizar as últimas atualizações realizadas. Acessível para o usuário verificado (apenas para visualização) e administrador;</li>
                            <li style={{ marginBottom: '0.25rem' }}><strong>Gerenciar Usuários:</strong> Para alterar as permissões. Acessível para o administrador somente;</li>
                            <li style={{ marginBottom: '0.25rem' }}><strong>Experimental:</strong> Para acessar funcionalidades ainda em desenvolvimento ou experimentais. Acessível para o usuário verificado e administrador;</li>
                            <li style={{ marginBottom: '0.25rem' }}><strong>Manual do Usuário:</strong> Para acessar este manual a partir do próprio sistema. Acessível para todos;</li>
                            <li style={{ marginBottom: '0.25rem' }}><strong>Sair:</strong> Para efetuar o <em>logout</em> do sistema. Acessível para todos;</li>
                        </ul>
                        <Fig n={6} alt="Figura 6: Menu auxiliar do DInoUSP – Administrador" />
                        <Fig n={7} alt="Figura 7: Menu auxiliar do DInoUSP – Verificado" />
                        <Fig n={8} alt="Figura 8: Menu auxiliar do DInoUSP – Não verificado" />
                        <p>
                            Ainda no cabeçalho é informada a versão de desenvolvimento da ferramenta, abaixo do botão de
                            acesso ao menu auxiliar, conforme a Figura 9.
                        </p>
                        <Fig n={9} alt="Figura 9: Versão do DInoUSP" />
                    </section>

                    {/* ── Seção 3.2: Painel de filtros ── */}
                    <section id="filtros" style={{ marginBottom: '3rem' }}>
                        <h3 style={{ color: COLOR_PALETTE.primary, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', borderBottom: `3px solid ${COLOR_PALETTE.primary}`, paddingBottom: '0.5rem' }}>
                            3.2. Painel de filtros
                        </h3>
                        <p>
                            Através do painel de filtros (Figura 10), o usuário pode indicar características para buscar
                            projetos que atendam um perfil de semelhança para que o DInoUSP faça suas análises relacionadas
                            aos níveis de prontidão. Estão disponíveis diversas categorias de filtros que podem ser expandidas e
                            retraídas ao clicar no respectivo <em>Chevron</em> (˅).
                        </p>
                        <Fig n={10} alt="Figura 10: Painel de filtros do DInoUSP" />

                        <h6 style={{ color: COLOR_PALETTE.primary, marginTop: '1.5rem' }}>Categorias de filtros:</h6>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary, marginTop: '1rem' }}>Seleção de parâmetros:</h6>
                            <p style={{ marginLeft: '1.5rem' }}>Permite indicar os valores iniciais e finais desejados para o TRL e CRL. Influencia a filtragem de projetos, buscando aqueles que contenham pelo menos uma transição dentro do intervalo especificado. E aponta o intervalo desejado para as simulações de duração total das transições.</p>
                            <Fig n={11} alt="Figura 11: Filtro de seleção de parâmetros do DInoUSP" />
                        </div>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary }}>Grau de inovação:</h6>
                            <p style={{ marginLeft: '1.5rem' }}>Permite indicar o(s) grau(s) de inovação desejado(s) para a filtragem de projetos na base de dados.</p>
                            <Fig n={12} alt="Figura 12: Filtro de grau de inovação do DInoUSP" />
                        </div>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary }}>Categoria:</h6>
                            <p style={{ marginLeft: '1.5rem' }}>Permite indicar a(s) categoria(s) de entrega desejada(s) para a filtragem de projetos na base de dados.</p>
                            <Fig n={13} alt="Figura 13: Filtro de categoria de entrega" />
                        </div>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary }}>Tipologia:</h6>
                            <p style={{ marginLeft: '1.5rem' }}>Permite indicar a(s) tipologia(s) desejada(s) para a filtragem de projetos na base de dados.</p>
                            <Fig n={14} alt="Figura 14: Filtro de tipologia do DInoUSP" />
                        </div>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary }}>Status:</h6>
                            <p style={{ marginLeft: '1.5rem' }}>Permite indicar estados de projeto para a filtragem com base no que está registrado na base de dados.</p>
                            <Fig n={15} alt="Figura 15: Filtro de status do DInoUSP" />
                        </div>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary }}>Portfólio:</h6>
                            <p style={{ marginLeft: '1.5rem' }}>Permite indicar o(s) portifólio(s) desejado(s) para a filtragem de projetos na base de dados.</p>
                            <Fig n={16} alt="Figura 16: Filtro de portfólio do DInoUSP" />
                        </div>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary }}>Iniciativa Tecnológica:</h6>
                            <p style={{ marginLeft: '1.5rem' }}>Permite indicar a(s) iniciativa(s) tecnológica(s) desejada(s) para a filtragem de projetos na base de dados.</p>
                            <Fig n={17} alt="Figura 17: Filtro de iniciativa tecnológica do DInoUSP" />
                        </div>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary }}>Data:</h6>
                            <ul style={{ marginLeft: '1.5rem', color: '#495057', paddingLeft: '2rem', listStyleType: 'disc', listStylePosition: 'outside' }}>
                                <li style={{ marginBottom: '0.25rem' }}>Permite indicar um período para filtrar projetos que tiveram sua data de disponibilização dentro desse período;</li>
                                <li style={{ marginBottom: '0.25rem' }}>Também é oferecida a opção de incluir dados de previsões de gerentes nas análises do DInoUSP.</li>
                            </ul>
                            <Fig n={18} alt="Figura 18: Filtro de data do DInoUSP" />
                        </div>

                        <p style={{ marginTop: '1.5rem' }}>
                            Após indicar os filtros desejados, para executar a análise basta clicar no botão <strong>Aplicar filtros</strong> e aguardar o processamento ser concluído.
                        </p>
                    </section>

                    {/* ── Seção 3.3: Painel de resultados ── */}
                    <section id="resultados" style={{ marginBottom: '3rem' }}>
                        <h3 style={{ color: COLOR_PALETTE.primary, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', borderBottom: `3px solid ${COLOR_PALETTE.primary}`, paddingBottom: '0.5rem' }}>
                            3.3. Painel de resultados
                        </h3>
                        <p>
                            Após aplicar algum filtro, o painel de resultados será atualizado e sua visualização será
                            apresentada numa estrutura de quatro (4) abas ou categorias (Figura 19): Resumo; Estatística
                            descritiva; Distribuições; e Simulação. Caso os filtros selecionados capturem um número pequeno de
                            projetos, que inviabilize as análises, será apresentada uma mensagem de erro indicando o problema,
                            conforme a Figura 20.
                        </p>
                        <Fig n={19} alt="Figura 19: Painel de resultados do DInoUSP" />
                        <Fig n={20} alt="Figura 20: Painel de resultados, com mensagem de erro, do DInoUSP" />
                    </section>

                    {/* ── Seção 3.3.1: Resumo ── */}
                    <section id="resumo" style={{ marginBottom: '3rem' }}>
                        <h3 style={{ color: COLOR_PALETTE.primary, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', borderBottom: `3px solid ${COLOR_PALETTE.primary}`, paddingBottom: '0.5rem' }}>
                            3.3.1. Resumo
                        </h3>
                        <p>
                            Na aba "Resumo" são apresentadas informações gerais dos projetos que foram capturados pelo filtro desejado,
                            sem nenhum processamento de dados. Estão disponíveis duas (2) opções de visualização (DADOS e TRL/CRL) conforme a Figura 21.
                        </p>
                        <Fig n={21} alt="Figura 21: Opções de visualização – Resumo – Resultados do DInoUSP" />
                        <p>
                            Na visualização "DADOS" é apresentada uma tabela com algumas informações dos projetos (Figura 22).
                            Na parte superior da tabela à esquerda é apresentado o número de projetos capturados com o filtro selecionado
                            e à direita algumas configurações de visualização da tabela (número de projetos por página e colunas desejadas).
                            As colunas dessa tabela são:
                        </p>
                        <ul style={{ color: '#495057', paddingLeft: '2rem', listStyleType: 'disc', listStylePosition: 'outside' }}>
                            <li style={{ marginBottom: '0.25rem' }}>INDEX – Código do projeto;</li>
                            <li style={{ marginBottom: '0.25rem' }}>TIPOLOGIA – Conforme os filtros;</li>
                            <li style={{ marginBottom: '0.25rem' }}>STATUS – Conforme os filtros;</li>
                            <li style={{ marginBottom: '0.25rem' }}>GRAU DE INOVAÇÃO – Conforme os filtros;</li>
                            <li style={{ marginBottom: '0.25rem' }}>CÓDIGO IT – Código da iniciativa tecnológica, conforme os filtros;</li>
                            <li style={{ marginBottom: '0.25rem' }}>INICIATIVA TECNOLÓGICA – Conforme os filtros;</li>
                            <li style={{ marginBottom: '0.25rem' }}>ID PORTIFÓLIO – Código do portifólio, conforme os filtros;</li>
                            <li style={{ marginBottom: '0.25rem' }}>PORTFÓLIO – Conforme os filtros;</li>
                            <li style={{ marginBottom: '0.25rem' }}>CONTAGEM TRL – Número de transições de TRL encontradas no projeto;</li>
                            <li style={{ marginBottom: '0.25rem' }}>TRL MIN – Menor TRL registrado no projeto;</li>
                            <li style={{ marginBottom: '0.25rem' }}>TRL MAX – Maior TRL registrado no projeto;</li>
                            <li style={{ marginBottom: '0.25rem' }}>CONTAGEM CRL – Número de transições de CRL encontradas no projeto;</li>
                            <li style={{ marginBottom: '0.25rem' }}>CRL MIN – Menor CRL registrado no projeto;</li>
                            <li style={{ marginBottom: '0.25rem' }}>CRL MAX – Maior CRL registrado no projeto.</li>
                        </ul>
                        <Fig n={22} alt="Figura 22: Resumo – DADOS – Resultados do DInoUSP" />
                        <p>
                            É possível remover e adicionar projetos específicos na análise caso seja de interesse para o usuário.
                            Para fazer isso, é necessário clicar no botão <strong>Selecionar Projetos</strong> na parte superior direita. Ao clicar,
                            as opções de adição e remoção serão exibidas conforme a Figura 23. Para adicionar projetos, o usuário deve clicar
                            no botão <strong>+ Adicionar</strong>, que abre uma lista com os projetos disponíveis (Figura 24), selecionar os projetos
                            e confirmar a operação. E para remover projetos, o usuário deve marcar os projetos desejados na tabela,
                            clicar no botão <strong>– Remover</strong>, e confirmar a operação. Essa operação implica no reprocessamento dos
                            cálculos do DInoUSP, então é comum que leve algum tempo.
                        </p>
                        <Fig n={23} alt="Figura 23: Resumo – Adicionar/Remover projetos no DInoUSP" />
                        <Fig n={24} alt="Figura 24: Resumo – Lista para adição no DInoUSP" />
                        <p>
                            Clicando em uma das linhas da tabela, é apresentada uma imagem com a progressão conjunta de
                            TRL e CRL, realizada e prevista (caso seja feita essa opção nos filtros), do projeto da linha
                            selecionada conforme a Figura 25.
                        </p>
                        <Fig n={25} alt="Figura 25: Resumo – Progressão TRL x CRL de um projeto no DInoUSP" />
                        <p>
                            E na visualização "TRL/CRL" é apresentado um <em>heatmap</em> da progressão conjunta de TRL e CRL de
                            todos os projetos capturados pelo filtro (Figura 26).
                        </p>
                        <Fig n={26} alt="Figura 26: Resumo – TRL/CRL – Resultados do DInoUSP" />
                        <p>
                            Caso haja interesse em visualizar a curva para uma das linhas ou colunas, isso pode ser feito
                            clicando sobre o <em>heatmap</em>, o que abre uma janela conforme a Figura 27.
                        </p>
                        <Fig n={27} alt="Figura 27: Resumo – Curvas individuais do heatmap – Resultados do DInoUSP" />
                    </section>

                    {/* ── Seção 3.3.2: Estatística descritiva ── */}
                    <section id="estatisticas" style={{ marginBottom: '3rem' }}>
                        <h3 style={{ color: COLOR_PALETTE.primary, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', borderBottom: `3px solid ${COLOR_PALETTE.primary}`, paddingBottom: '0.5rem' }}>
                            3.3.2. Estatística descritiva
                        </h3>
                        <p>Na aba "Estatística descritiva" são apresentadas informações estatísticas básicas para a progressão em níveis de prontidão dos projetos:</p>
                        <ul style={{ color: '#495057', paddingLeft: '2rem', listStyleType: 'disc', listStylePosition: 'outside' }}>
                            <li style={{ marginBottom: '0.25rem' }}>Contagem: Número de vezes que a transição foi observada no conjunto de projetos filtrado;</li>
                            <li style={{ marginBottom: '0.25rem' }}>Média: Tempo médio para a transição no conjunto de projetos filtrados;</li>
                            <li style={{ marginBottom: '0.25rem' }}>Mínimo: Menor tempo para a transição no conjunto de projetos filtrados;</li>
                            <li style={{ marginBottom: '0.25rem' }}>Máximo: Maior tempo para a transição no conjunto de projetos filtrados;</li>
                            <li style={{ marginBottom: '0.25rem' }}>Desvio padrão: Do tempo para a transição no conjunto de projetos filtrados;</li>
                            <li style={{ marginBottom: '0.25rem' }}>Coeficiente de variação: Do tempo para a transição no conjunto de projetos filtrados;</li>
                            <li style={{ marginBottom: '0.25rem' }}>P25: Primeiro quartil dos tempos para a transição no conjunto de projetos filtrados;</li>
                            <li style={{ marginBottom: '0.25rem' }}>P50: Segundo quartil dos tempos para a transição no conjunto de projetos filtrados;</li>
                            <li style={{ marginBottom: '0.25rem' }}>P75: Terceiro quartil dos tempos para a transição no conjunto de projetos filtrados.</li>
                        </ul>
                        <p>Estão disponíveis duas (2) opções de visualização (TABELA e GRÁFICOS) conforme a Figura 28.</p>
                        <Fig n={28} alt="Figura 28: Opções de visualização – Estatística descritiva – Resultados do DInoUSP" />
                        <p>A primeira apresentação é tabular, como ilustrado na Figura 29, enquanto a segunda consiste em histogramas (Figura 30).</p>
                        <Fig n={29} alt="Figura 29: Estatística descritiva – TABELA – Resultados do DInoUSP" />
                        <Fig n={30} alt="Figura 30: Estatística descritiva – GRÁFICOS – Resultados do DInoUSP" />
                    </section>

                    {/* ── Seção 3.3.3: Distribuições ── */}
                    <section id="distribuicoes" style={{ marginBottom: '3rem' }}>
                        <h3 style={{ color: COLOR_PALETTE.primary, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', borderBottom: `3px solid ${COLOR_PALETTE.primary}`, paddingBottom: '0.5rem' }}>
                            3.3.3. Distribuições
                        </h3>
                        <p>
                            Na aba "Distribuições" são apresentados os resultados dos ajustes da progressão em níveis de
                            prontidão dos projetos. As distribuições são calculadas para todas as transições que tiverem oito (8)
                            ou mais registros no conjunto de projetos filtrado. As visualizações disponíveis estão relacionadas
                            aos tipos de distribuições (EMPÍRICAS, AJUSTADAS e <em>RANDOM FOREST</em>), sendo que para todas estão
                            disponíveis as visualizações tabulares e gráficas, conforme a Figura 31.
                        </p>
                        <Fig n={31} alt="Figura 31: Opções de visualização – Distribuições – Resultados do DInoUSP" />
                        <p>Nas distribuições <strong>empíricas</strong> são apresentadas as médias, P10, P50, P75 e P90 dos tempos de duração para cada transição, conforme a Figura 32 e Figura 33.</p>
                        <Fig n={32} alt="Figura 32: Distribuições – EMPÍRICAS (TABELA) – Resultados do DInoUSP" />
                        <Fig n={33} alt="Figura 33: Distribuições – EMPÍRICAS – Resultados do DInoUSP" />
                        <p>
                            Na visualização tabular das distribuições <strong>ajustadas</strong> (Figura 34) são apresentadas as funções
                            estatísticas com melhor ajuste ao conjunto de dados para cada transição, os parâmetros de ajuste
                            (um balão com as descrições dos parâmetros se abre ao aproximar o mouse dos parâmetros desejados),
                            e coeficientes de aderência (LOGLIK – log-verossimilhança; AIC – Critério de Informação de Akaike;
                            BIC – Critério de Informação Bayesiano; KS STAT – Estatística de Kolmogorov-Smirnov; KS P-VALOR –
                            Valor-p de Kolmogorov-Smirnov; SAPHIRO STAT – Estatística de Shapiro-Wilk; SAPHIRO P-VALOR –
                            Valor-p de Shapiro-Wilk).
                        </p>
                        <Fig n={34} alt="Figura 34: Distribuições – AJUSTADAS (TABELA) – Resultados do DInoUSP" />
                        <p>
                            E na visualização gráfica das distribuições <strong>ajustadas</strong>, também é possível especificar o valor
                            em dias clicando no botão <strong>Especificar valor</strong> (Figura 35), são apresentadas as curvas da
                            PDF (Função densidade de probabilidade) e CDF (Função densidade acumulada). Aproximar o <em>mouse</em>
                            de algum ponto nas curvas abre uma caixa exibindo os valores correspondentes ao ponto. Da mesma
                            forma que na apresentação tabular, aproximar o <em>mouse</em> dos parâmetros abre um balão com as
                            descrições dos mesmos (Figura 36).
                        </p>
                        <Fig n={35} alt="Figura 35: Distribuições – AJUSTADAS – Resultados do DInoUSP" />
                        <Fig n={36} alt="Figura 36: Distribuições – Descrição dos parâmetros" />
                        <p>
                            A visualização das distribuições <strong><em>Random Forest</em></strong> segue o mesmo padrão das distribuições
                            empíricas, apresentando as médias, P10, P50, P75 e P90 dos tempos de duração para cada transição,
                            conforme a Figura 37 e Figura 38.
                        </p>
                        <Fig n={37} alt="Figura 37: Distribuições – RANDOM FOREST (TABELA) – Resultados do DInoUSP" />
                        <Fig n={38} alt="Figura 38: Distribuições – RANDOM FOREST – Resultados do DInoUSP" />
                    </section>

                    {/* ── Seção 3.3.4: Simulação ── */}
                    <section id="simulacao" style={{ marginBottom: '3rem' }}>
                        <h3 style={{ color: COLOR_PALETTE.primary, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', borderBottom: `3px solid ${COLOR_PALETTE.primary}`, paddingBottom: '0.5rem' }}>
                            3.3.4. Simulação
                        </h3>
                        <p>Na aba "Simulação" são apresentados os resultados para:</p>
                        <ul style={{ color: '#495057', paddingLeft: '2rem', listStyleType: 'disc', listStylePosition: 'outside' }}>
                            <li style={{ marginBottom: '0.25rem' }}>O tempo total para a progressão do TRL no intervalo indicado nos filtros, e as componentes empíricas (transições de TRL de interesse) utilizadas na simulação;</li>
                            <li style={{ marginBottom: '0.25rem' }}>O tempo total para a progressão do CRL no intervalo indicado nos filtros, e as componentes empíricas (transições de CRL de interesse) utilizadas na simulação;</li>
                            <li style={{ marginBottom: '0.25rem' }}>O tempo total para a progressão conjunta de TRL e CRL nos intervalos indicados nos filtros.</li>
                        </ul>
                        <p>
                            No campo superior direito, é possível especificar uma data de referência clicando no botão
                            <strong> Especificar data de referência</strong> e preenchendo o campo expandido, conforme a Figura 39.
                        </p>
                        <Fig n={39} alt="Figura 39: Especificação de data – Simulação – Resultados do DInoUSP" />
                        <p>Estão disponíveis duas (2) opções de visualização (TABELA e GRÁFICOS), conforme a Figura 40.</p>
                        <Fig n={40} alt="Figura 40: Opções de visualização – Simulação – Resultados do DInoUSP" />
                        <p>Na visualização "TABELA" é apresentada uma tabela com os valores resultados de média, P10, P50, P90 e campo para inserir o percentil desejado para todas as componentes de transição das durações totais, conforme a Figura 41.</p>
                        <Fig n={41} alt="Figura 41: Simulação – TABELA – Resultados do DInoUSP" />
                        <p>Na visualização "GRÁFICOS", são apresentadas as distribuições para todas as componentes de transição das durações totais, as distribuições empíricas para os resultados das durações totais (Figura 42), e as distribuições ajustadas para os resultados das durações totais (Figura 43).</p>
                        <Fig n={42} alt="Figura 42: Simulação – GRÁFICO – Empírica, resultados do DInoUSP" />
                        <Fig n={43} alt="Figura 43: Simulação – GRÁFICO – Ajustada, resultados do DInoUSP" />
                        <p>
                            O gráfico da distribuição ajustada (Figura 43) tem as mesmas funcionalidades apresentadas na
                            aba "Distribuições" (Figura 35 e Figura 36). Ou seja, aproximar o ponteiro do <em>mouse</em> de um ponto na
                            curva abre os valores daquele ponto e aproximar o ponteiro dos parâmetros abre uma descrição dos
                            parâmetros, bem como o botão para especificar valor.
                        </p>
                        <p>
                            Existe uma restrição de número mínimo de transições observadas para a execução da simulação.
                            Caso esse mínimo não seja atendido, para qualquer uma das transições de interesse indicadas nos
                            filtros (TRL ou CRL), a aba "Simulação" apresentará uma mensagem de erro conforme a Figura 44.
                        </p>
                        <Fig n={44} alt="Figura 44: Simulação – Mensagem de erro no DInoUSP" />
                    </section>

                    {/* ── Seção 3.3.5: Exportar resultados ── */}
                    <section id="exportar" style={{ marginBottom: '3rem' }}>
                        <h3 style={{ color: COLOR_PALETTE.primary, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', borderBottom: `3px solid ${COLOR_PALETTE.primary}`, paddingBottom: '0.5rem' }}>
                            3.3.5. Exportar resultados
                        </h3>
                        <p>
                            Está implementada no DInoUSP a funcionalidade de exportar os resultados de uma análise.
                            Clicando no botão <strong>Exportar</strong>, na parte superior à direita no painel de resultados, uma janela como a
                            da Figura 45 se abrirá para iniciar o processo. Para exportar algum resultado, deve-se selecionar um
                            dos formatos oferecidos (Figura 46), escolher os resultados desejados e confirmar a operação.
                            Dependendo dos filtros, o volume de dados pode ser significativo, o que pode tornar o processo demorado.
                        </p>
                        <Fig n={45} alt="Figura 45: Exportar resultados do DInoUSP" />
                        <Fig n={46} alt="Figura 46: Formatos de exportação do DInoUSP" />
                        <p>Por limitações dos próprios formatos de arquivo, cada um deles implica em algumas diferenças no conteúdo que é exportado, sendo que:</p>
                        <ul style={{ color: '#495057', paddingLeft: '2rem', listStyleType: 'disc', listStylePosition: 'outside' }}>
                            <li style={{ marginBottom: '0.25rem' }}>Para Excel são exportadas as tabelas e imagens dos gráficos;</li>
                            <li style={{ marginBottom: '0.25rem' }}>Para JSON são exportados os dados de comunicação entre o <em>backend</em> e <em>frontend</em>;</li>
                            <li style={{ marginBottom: '0.25rem' }}>Para PDF são exportadas apenas as imagens dos gráficos.</li>
                        </ul>
                    </section>

                    {/* ── Seção 4: Meu Perfil ── */}
                    <section id="perfil" style={{ marginBottom: '3rem' }}>
                        <h3 style={{ color: COLOR_PALETTE.primary, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', borderBottom: `3px solid ${COLOR_PALETTE.primary}`, paddingBottom: '0.5rem' }}>
                            4. Meu Perfil
                        </h3>
                        <p>
                            Na página "Meu Perfil" (Figura 47), o usuário pode atualizar as informações do seu cadastro e a sua senha.
                            Para isso, basta alterar os campos desejados e clicar no botão <strong>Aplicar alterações</strong> ou <strong>Alterar senha</strong>,
                            dependendo da alteração desejada. Ao clicar em um dos botões de alteração, uma nova janela se abrirá,
                            pedindo a senha atual do usuário (Figura 48) para confirmar e aplicar as alterações no sistema.
                        </p>
                        <Fig n={47} alt="Figura 47: Janela Meu Perfil no DInoUSP" />
                        <Fig n={48} alt="Figura 48: Meu Perfil – Confirmação de senha atual" />
                    </section>

                    {/* ── Seção 5: Banco de Dados ── */}
                    <section id="bancoDados" style={{ marginBottom: '3rem' }}>
                        <h3 style={{ color: COLOR_PALETTE.primary, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', borderBottom: `3px solid ${COLOR_PALETTE.primary}`, paddingBottom: '0.5rem' }}>
                            5. Banco de Dados
                        </h3>
                        <p>A página "Banco de Dados" (Figura 49) tem dois objetivos principais:</p>
                        <ul style={{ color: '#495057', paddingLeft: '2rem', listStyleType: 'disc', listStylePosition: 'outside' }}>
                            <li style={{ marginBottom: '0.25rem' }}>Para um usuário verificado: apresentar informações das últimas atualizações;</li>
                            <li style={{ marginBottom: '0.25rem' }}>Para um usuário administrador: disponibilizar meios de atualizar a base de dados usada pelo DInoUSP em suas análises.</li>
                        </ul>
                        <Fig n={49} alt="Figura 49: Janela Banco de dados no DInoUSP" />
                        <p>Estão disponíveis quatro (4) formas de executar a atualização da base de dados:</p>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary }}>1. Atualização manual</h6>
                            <p style={{ marginLeft: '1.5rem' }}>Utilize esta opção para realizar o <em>upload</em> manual de novos conjuntos de dados diretamente do seu computador. Para usar essa opção, é necessário inserir os três arquivos de dados usados pelo DInoUSP (conforme a Figura 50) e, opcionalmente, escrever uma mensagem de identificação que será incorporada ao registro dessa atualização. O processo de obtenção desses arquivos envolve o acesso ao AIDA e não será detalhado neste manual;</p>
                            <Fig n={50} alt="Figura 50: Banco de dados – Atualização manual" />
                        </div>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary }}>2. Atualização automática</h6>
                            <p style={{ marginLeft: '1.5rem' }}>Nessa modalidade, os três arquivos necessários são extraídos diretamente do AIDA sem a necessidade de inserir cada um dos arquivos individualmente (Figura 51). Sendo outra atualização executada sob demanda, também é oferecida a opção de escrever uma mensagem de identificação que será incorporada ao registro dessa atualização;</p>
                            <Fig n={51} alt="Figura 51: Banco de dados – Atualização automática" />
                        </div>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary }}>3. Atualização programada</h6>
                            <p style={{ marginLeft: '1.5rem' }}>No que diz respeito à forma de executar o processo de atualização, a opção programada faz uso do mesmo algoritmo da automática. Neste caso a diferença é que, ao clicar no ícone de engrenagem, um usuário administrador pode configurar a execução periódica de atualizações dos dados, conforme a Figura 52.</p>
                            <Fig n={52} alt="Figura 52: Banco de dados – Atualização programada" />
                        </div>

                        <div className="mb-3">
                            <h6 style={{ color: COLOR_PALETTE.primary }}>4. Restaurar atualizações anteriores</h6>
                            <p style={{ marginLeft: '1.5rem' }}>Caso haja interesse ou necessidade, um usuário administrador pode restaurar uma versão anterior da base de dados do sistema clicando no botão <strong>Restaurar</strong> da versão desejada no histórico de atualizações. Isso abrirá uma mensagem pedindo a confirmação da operação conforme a Figura 53.</p>
                            <Fig n={53} alt="Figura 53: Banco de dados – Restaurar versões anteriores" />
                        </div>
                    </section>

                    {/* ── Seção 6: Gerenciar Usuários ── */}
                    <section id="gerenciarUsuarios" style={{ marginBottom: '3rem' }}>
                        <h3 style={{ color: COLOR_PALETTE.primary, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', borderBottom: `3px solid ${COLOR_PALETTE.primary}`, paddingBottom: '0.5rem' }}>
                            6. Gerenciar Usuários
                        </h3>
                        <p>
                            Na página "Gerenciar Usuários", um usuário administrador pode alterar o nível de acesso dos
                            usuários cadastrados no sistema. Para fazer essas alterações, basta usar o menu de seleção à direita,
                            na linha respectiva do usuário que se deseja alterar. Também é possível usar o campo de busca para
                            encontrar um usuário específico pelo nome ou e-mail.
                        </p>
                        <Fig n={54} alt="Figura 54: Janela Gerenciar Usuários no DInoUSP" />
                    </section>

                    {/* ── Seção 7: Experimental ── */}
                    <section id="experimental" style={{ marginBottom: '3rem' }}>
                        <h3 style={{ color: COLOR_PALETTE.primary, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', borderBottom: `3px solid ${COLOR_PALETTE.primary}`, paddingBottom: '0.5rem' }}>
                            7. Experimental
                        </h3>
                        <p>
                            A página "Experimental" disponibiliza recursos em desenvolvimento, que podem estar incompletos
                            ou sofrer mudanças em futuras atualizações, conforme a Figura 55.
                        </p>
                        <Fig n={55} alt="Figura 55: Janela experimental" />
                    </section>
                </div>
            </Modal.Body>

            <Modal.Footer style={{ borderTop: `2px solid ${COLOR_PALETTE.neutral[200]}` }}>
                <Button
                    variant="danger"
                    onClick={onHide}
                    style={{
                        backgroundColor: '#dc3545',
                        borderColor: '#dc3545',
                        padding: '10px 30px',
                        fontWeight: 'bold',
                        borderRadius: '8px'
                    }}
                >
                    Fechar
                </Button>
                <Button
                    variant="primary"
                    onClick={() => {
                        const link = document.createElement('a');
                        link.href = '/manual-usuario.pdf';
                        link.download = 'manual-usuario-dinousp.pdf';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }}
                    style={{
                        backgroundColor: COLOR_PALETTE.primary,
                        borderColor: COLOR_PALETTE.primary,
                        padding: '10px 30px',
                        fontWeight: 'bold',
                        borderRadius: '8px'
                    }}
                >
                    Baixar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserManualModal;